import React, { useState, useEffect, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import BasicForm from '../basicForm/basicForm';
import style from './userDetail.module.scss';
import { validationMessages } from '../../../utils/constants';
import ValidationErrorLabel from '../../common/ValidationErrorLabel/ValidationErrorLabel';
import { Input } from '../../common/Form/Input/Input';
import ButtonComponent from '../../common/buttons/ButtonComponent/ButtonComponent';
import { buttonVariants } from '../../common/buttons/types';
import logoIcon from '../../../Assets/images/Airswift Primary Logo (Gradient Bird Only).png';
import linkedInIcon from '../../../Assets/images/linkedin-logo.svg';
import { loginRequest } from '../../../authConfig';
import { emailRegExp } from '../../../utils/regexps';

function UserDetail(props) {
  const [phone, setphone] = useState(null);
  const [Phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [PhoneCountryCode, setPhoneCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const initialErrorValidationValue = {
    phone: false,
    firstName: false,
    lastName: false,
    email: false
  };
  const [phoneReqError, setPhoneReqError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [firstNameError, setFirstnameError] = useState(false);
  const [lastNameError, setLastnameError] = useState(false);
  const [errorValidation, setErrorValidation] = useState(initialErrorValidationValue);
  const { nextHandle, cancel, data, step, totalStep, jobTitle } = props;
  const { instance } = useMsal();

  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  useEffect(() => {
    if (data.PhoneCountryCode && data.Phone) {
      setPhone(data.Phone);
      setPhoneCode(data.PhoneCountryCode);
      setphone(data.PhoneCountryCode + data.Phone);
    }

    if (data.firstName) setFirstName(data.firstName);
    if (data.lastName) setLastName(data.lastName);
    if (data.email) setEmail(data.email);
    if (data.countryName) setCountryName(data.countryName);
  }, [data]);

  const validate = () => {
    const validationObj = {
      ...initialErrorValidationValue
    };
    if (!Phone) {
      validationObj.phone = true;
      setPhoneReqError(validationMessage.PHONE_NUMBER_REQ);
    }
    if (Phone && (PhoneCountryCode === 'undefined' || Phone.length < 4)) {
      validationObj.phone = true;
      setPhoneError(validationMessage.PHONE_NUMBER_INVALID);
    }
    if (!firstName) {
      validationObj.firstName = true;
      setFirstnameError('First name is required');
    }
    if (!lastName) {
      validationObj.lastName = true;
      setLastnameError('Last name is required');
    }
    if (!email || (!!email.length && !email.match(emailRegExp)?.length)) {
      validationObj.email = true;
      setEmailError('Email is not valid');
    }
    return validationObj;
  };

  const handleRedirectToApply = (id) => {
    localStorage.setItem('OpenedJobId', JSON.stringify(id));
    instance
      .loginRedirect({
        ...loginRequest,
        redirectUri: `${window.location.origin}/dashboard/jobs`,
        state: JSON.stringify({ navigate: `/dashboard/jobs/${id}?action=apply` })
      })
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <BasicForm
      cancel={cancel}
      title={`${t('jobs.quickapply.userdetailheader')} ${jobTitle}`}
      progress="20"
      subtitle={t('jobs.quickapply.applyfewclicks')}
      step={step}
      totalStep={totalStep}
    >
      <div className={style.bgWrapper}>
        <div className="mb-4">
          <Input
            onChange={(e) => {
              setFirstName(e.target.value);
              setFirstnameError(false);
            }}
            value={firstName || data?.firstName}
            isClosedVariant
            type="text"
            placeholder="First Name"
          />
          {firstNameError && <ValidationErrorLabel message={firstNameError} />}
        </div>
        <div className="mb-4">
          <Input
            onChange={(e) => {
              setLastName(e.target.value);
              setLastnameError(false);
            }}
            value={lastName || data?.lastName}
            isClosedVariant
            type="text"
            placeholder="Last Name"
          />
          {lastNameError && <ValidationErrorLabel message={lastNameError} />}
        </div>
        <div className="mb-4">
          <Input
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(false);
            }}
            value={email || data?.email}
            isClosedVariant
            type="email"
            placeholder="Email"
          />
          {emailError && <ValidationErrorLabel message={emailError} />}
        </div>
        <div className="mb-4">
          <PhoneInput
            inputStyle={{ width: '100%', marginBottom: '20px' }}
            value={phone}
            placeholder={t('jobs.quickapply.phonenumber')}
            onChange={(newPhone, code) => {
              setPhoneReqError(false);
              setPhoneError(false);
              setphone(newPhone);
              setPhoneCode(`${code.dialCode ? '+' : ''}${code.dialCode}`);
              setPhone(`${newPhone}`.replace(new RegExp(`${code.dialCode}`), ''));
              setCountryName(`${code.name}`);
            }}
            id="phoneNumber"
          />
          {phoneError && <ValidationErrorLabel message={phoneError} />}
          {phoneReqError && <ValidationErrorLabel message={phoneReqError} />}
        </div>
      </div>
      <ButtonComponent
        classes="w-100"
        variant={buttonVariants.PRIMARY}
        action={async () => {
          const validationObj = validate();
          const validationWrongValues = Object.values(validationObj).filter(Boolean);
          if (!validationWrongValues.length) {
            nextHandle({ firstName, lastName, email, Phone, PhoneCountryCode, countryName });
          } else {
            const validationWrongEntries = Object.entries(validationObj).filter((v) => Boolean(v[1]));
            const newErrorValidationValue = {};
            validationWrongEntries.forEach((item) => {
              const [key, value] = item;
              newErrorValidationValue[key] = value;
            });
            setErrorValidation(newErrorValidationValue);
            setTimeout(() => setErrorValidation(initialErrorValidationValue), 2000);
          }
        }}
        text={t('common.general.next')}
      />
      <div className={style.footerWrapper}>
        <span className={style.textAlone}>Or</span>
        <ButtonComponent
          action={() => handleRedirectToApply(data?.JobId)}
          classes="w-100 mb-3"
          variant={buttonVariants.SECONDARY}
          text="Apply with my account"
        >
          <img src={logoIcon} alt="airswift" width="25px" />
        </ButtonComponent>
        {/* Temporarily commented LinkedIn - bug 13506 */}
        {/* <ButtonComponent */}
        {/*   classes="w-100 mb-sm-4" */}
        {/*   variant={buttonVariants.SECONDARY} */}
        {/*   text="Apply through LinkedIn"> */}
        {/*   <img src={linkedInIcon} alt="airswift" width="25px" /> */}
        {/* </ButtonComponent> */}
      </div>
    </BasicForm>
  );
}

export default UserDetail;
